import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HeartPulse, Flame, Droplets, ShieldPlus, Bone, Sparkles, Quote, Star } from "lucide-react";
import { WaveLine, KeySeq } from "../components/site/Wave";
import { api, money } from "../lib/api";
import { useCart } from "../context/CartContext";

const DEVICE_IMG = "https://cdn.shopify.com/s/files/1/0609/0679/9349/files/Product_image_WT_1020x693.jpg";
const PROTOCOLS = [
  { key: "health_wellness", label: "Health & Wellness", desc: "Daily vitality, sleep, brain & heart balance.", Icon: HeartPulse, color: "#10B981" },
  { key: "pain_inflammation", label: "Pain & Inflammation", desc: "Acute or chronic pain, arthritis, headaches, muscle tension.", Icon: Flame, color: "#EF4444" },
  { key: "detoxification", label: "Detoxification", desc: "Body cleanse, liver, kidney & lymph support.", Icon: Droplets, color: "#8B5CF6" },
  { key: "immune_boost", label: "Immune Boost", desc: "Viral & bacterial support, chronic infections like EBV or Lyme.", Icon: ShieldPlus, color: "#F59E0B" },
  { key: "repair_recovery", label: "Repair & Recovery", desc: "Post-surgery, injuries, fractures and sports performance.", Icon: Bone, color: "#3B82F6" },
  { key: "meditation", label: "Deep Meditation & Prayer", desc: "Relaxation, focus and clarity for your spiritual practice.", Icon: Sparkles, color: "#6366F1" },
];
const FREEDOM = [
  ["F", "Faith. Hope. Love.", "Our bodies are divinely designed to heal naturally."],
  ["R", "Restoration", "Give your body what it needs (and doesn't) to release + restore."],
  ["E", "Electric Health", "Cells need to be energized for cellular communication."],
  ["E", "Emotional Balance", "Stress harms our health, laughter improves it."],
  ["D", "Detoxification", "More vitality and longevity by removing toxins."],
  ["O", "Oxygenation", "A richer oxygen environment for your cells with deep breathing."],
  ["M", "Mindfulness", "Be intentional with your thoughts and words."],
];
const STORIES = [
  { q: "After both a discectomy and knee surgery, Wave Therapy was a big part of my recovery. Both of my surgeons were amazed at how soon I was back on my feet.", n: "Coach Mark", l: "Laguna Niguel, CA" },
  { q: "I've used Wave Therapy for me and my family for five years. I'm able to keep working because of the machine and personalized plan.", n: "Christa N.", l: "Newport Beach, CA" },
  { q: "As a Naturopathic Physician I've used my CuraWaves machine for years. I've noticed steady improvement in general vitality and reduced muscle tension and fatigue.", n: "Dr. Coleen Murphy Assalian ND, LAc", l: "San Juan Capistrano" },
];

const Hero = () => (
  <section className="relative overflow-hidden grain">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-24 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6 fade-up">
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#0D9488]">NASA-backed square wave technology</span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight mt-5">
          Your personalized path to <em className="text-[#0D9488] not-italic font-normal italic">freedom from pain</em> and greater vitality.
        </h1>
        <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
          Shop the Wave Therapy frequency generator, then let our AI Wave Therapy wellness coach build your daily, weekly or 30-day AUTO-code program — tuned to your goals, schedule and lab reports.
        </p>
        <div className="mt-9 flex flex-wrap gap-3">
          <Link to="/shop/at-home-wave-therapy" data-testid="hero-shop-device-btn" className="inline-flex items-center gap-2 rounded-full bg-[#0B1013] text-white px-7 py-3.5 font-semibold hover:bg-[#0F766E] transition-colors">
            Shop the device <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/questionnaire" data-testid="hero-build-plan-btn" className="inline-flex items-center gap-2 rounded-full border border-[#0B1013]/20 bg-white px-7 py-3.5 font-semibold hover:border-[#0D9488] hover:text-[#0D9488] transition-colors">
            Build my free plan
          </Link>
        </div>
        <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
          <span><b className="font-mono text-[#0B1013]">500+</b> frequencies</span>
          <span><b className="font-mono text-[#0B1013]">390+</b> AUTO codes</span>
          <span>Made in the USA</span>
        </div>
      </div>
      <div className="lg:col-span-6 relative">
        <div className="relative rounded-[28px] bg-[#0B1013] p-6 sm:p-8 shadow-2xl overflow-hidden">
          <WaveLine className="absolute inset-x-0 top-6 h-20" color="#06B6D4" opacity={0.85} stroke={3} />
          <WaveLine className="absolute inset-x-0 bottom-10 h-24" color="#0D9488" slow opacity={0.75} stroke={3} />
          <img src={DEVICE_IMG} alt="Wave Therapy frequency generator" className="relative rounded-2xl w-full object-cover" />
          <div className="relative mt-6 flex items-center justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#06B6D4]">Run a session</div>
              <div className="mt-2"><KeySeq keypad="AUTO, 646, RUN" /></div>
            </div>
            <div className="text-right font-mono text-xs text-slate-400">.04 – 10,000 Hz</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Protocols = () => (
  <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
    <div className="max-w-2xl">
      <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#0D9488]">Six protocol pathways</span>
      <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-3">Hundreds of waves. One plan crafted for you.</h2>
      <p className="mt-4 text-slate-600">Your AI-generated program selects exact AUTO › Code › RUN sessions from the official Wave Therapy Practitioner's Guide.</p>
    </div>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {PROTOCOLS.map(({ key, label, desc, Icon, color }, i) => (
        <Link to={`/questionnaire?goal=${key}`} key={key} data-testid={`protocol-card-${key}`} className="lift group bg-white rounded-2xl border border-[#E4DFD4] p-7 fade-up" style={{ animationDelay: `${i * 70}ms` }}>
          <span className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${color}18`, color }}><Icon className="w-5 h-5" /></span>
          <h3 className="font-display text-xl font-semibold mt-5">{label}</h3>
          <p className="text-sm text-slate-600 mt-2">{desc}</p>
          <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#0D9488] opacity-70 group-hover:opacity-100 transition-opacity">Start this plan <ArrowRight className="w-4 h-4" /></span>
        </Link>
      ))}
    </div>
  </section>
);

const HowItWorks = () => (
  <section id="how" className="bg-[#0B1013] text-white relative overflow-hidden">
    <WaveLine className="absolute inset-x-0 top-1/2 h-32 -translate-y-1/2" color="#0D9488" slow opacity={0.25} />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#06B6D4]">How your plan works</span>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-3">Easy to use. Flexible options.</h2>
        <p className="mt-4 text-slate-400">Default is 3 minutes per frequency. Enter minutes first to shorten or lengthen any session.</p>
      </div>
      <div className="lg:col-span-8 grid sm:grid-cols-3 gap-5">
        {[
          ["01", "Enter the sequence", "Press AUTO, the recommended code, then RUN.", "AUTO, 646, RUN"],
          ["02", "Adjust the time", "Prefix minutes per frequency — 1 for quicker, 5 for deeper sessions.", "5, AUTO, 154, RUN"],
          ["03", "Pause anytime", "Press any number key (1–9) to pause. Press RUN to resume.", "RUN"],
        ].map(([n, t, d, k]) => (
          <div key={n} className="rounded-2xl border border-white/10 bg-[#131A1E] p-6">
            <div className="font-mono text-[#06B6D4] text-sm">{n}</div>
            <h3 className="font-display text-xl mt-3">{t}</h3>
            <p className="text-sm text-slate-400 mt-2 mb-5">{d}</p>
            <KeySeq keypad={k} small />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Featured = () => {
  const [p, setP] = useState(null);
  const { add } = useCart();
  useEffect(() => { api.get("/products/at-home-wave-therapy").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p) return null;
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
      <div className="grid lg:grid-cols-12 gap-10 items-center bg-white rounded-[28px] border border-[#E4DFD4] p-6 sm:p-10">
        <img src={p.images[0]} alt={p.name} className="lg:col-span-6 w-full rounded-2xl object-cover aspect-[4/3] bg-[#EFECE5]" />
        <div className="lg:col-span-6">
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#D97706]"><Star className="w-3.5 h-3.5 fill-current" /> {p.badge}</span>
          <h2 className="font-display text-3xl font-semibold mt-3">{p.name}</h2>
          <p className="mt-4 text-slate-600">{p.short}</p>
          <ul className="mt-5 space-y-1.5 text-sm text-slate-700">{p.includes.slice(0, 5).map((x) => <li key={x}>— {x}</li>)}</ul>
          <div className="mt-7 flex items-center gap-4">
            <span data-testid="featured-price" className="font-mono text-2xl font-bold">{money(p.price)}</span>
            <button data-testid="featured-add-to-cart-btn" onClick={() => add(p)} className="rounded-full bg-[#0D9488] hover:bg-[#0F766E] text-white px-6 py-3 font-semibold transition-colors">Add to cart</button>
            <Link to={`/shop/${p.id}`} data-testid="featured-details-link" className="text-sm font-semibold text-[#0D9488] hover:underline">Details</Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const Freedom = () => (
  <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
    <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#0D9488]">The method</span>
    <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-3 max-w-2xl">The F.R.E.E.D.O.M. healing framework</h2>
    <div className="mt-10 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
      {FREEDOM.map(([l, t, d], i) => (
        <div key={i} className="bg-[#EFECE5] rounded-2xl p-5">
          <div className="font-display text-4xl text-[#0D9488]">{l}</div>
          <div className="font-semibold text-sm mt-3">{t}</div>
          <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{d}</p>
        </div>
      ))}
    </div>
  </section>
);

const Stories = () => (
  <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
    <h2 className="font-display text-3xl sm:text-4xl font-semibold">Real stories from the CuraWaves community</h2>
    <div className="mt-10 grid md:grid-cols-3 gap-5">
      {STORIES.map((s) => (
        <figure key={s.n} className="bg-white rounded-2xl border border-[#E4DFD4] p-7">
          <Quote className="w-6 h-6 text-[#0D9488]" />
          <blockquote className="mt-4 text-[15px] leading-relaxed text-slate-700">“{s.q}”</blockquote>
          <figcaption className="mt-5 text-sm"><b>{s.n}</b> <span className="text-slate-500">· {s.l}</span></figcaption>
        </figure>
      ))}
    </div>
    <p className="text-xs text-slate-500 mt-4">Individual results vary.</p>
  </section>
);

const FinalCta = () => (
  <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-8">
    <div className="rounded-[28px] bg-[#0D9488] text-white p-10 sm:p-14 relative overflow-hidden">
      <WaveLine className="absolute inset-x-0 bottom-0 h-24" color="#ffffff" opacity={0.3} />
      <img src="/logo-white.png" alt="CuraWaves" className="relative h-5 w-auto mb-6 opacity-90" />
      <h2 className="relative font-display text-3xl sm:text-4xl font-semibold max-w-xl">Take one step toward feeling great.</h2>
      <p className="relative mt-3 text-white/80 max-w-xl">The questionnaire only takes a few minutes. Add lab reports or additional context for more precise code recommendations. Your Wave Therapy plan is emailed to you for easy reference.</p>
      <Link to="/questionnaire" data-testid="final-cta-btn" className="relative mt-8 inline-flex items-center gap-2 rounded-full bg-white text-[#0B1013] px-7 py-3.5 font-semibold hover:bg-[#0B1013] hover:text-white transition-colors">
        Begin my health questionnaire <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
);

export default function Landing() {
  return (
    <div data-testid="landing-page">
      <Hero /><Protocols /><HowItWorks /><Featured /><Freedom /><Stories /><FinalCta />
    </div>
  );
}
